import {
  AlertCircle,
  Crown,
  Eye,
  Mail,
  RefreshCw,
  UserPlus,
  Users,
} from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/shared/components/ui/select'
import { Textarea } from '@/shared/components/ui/textarea'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { teamService } from '../services/teamService'
import type { TeamMember } from '../types/team.types'

interface InviteTeamMemberDialogProps {
  onMemberInvited?: (member: TeamMember) => void
}

export function InviteTeamMemberDialog({
  onMemberInvited,
}: InviteTeamMemberDialogProps) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<'admin' | 'member' | 'viewer'>('member')
  const [message, setMessage] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const resetForm = () => {
    setEmail('')
    setName('')
    setRole('member')
    setMessage('')
    setErrors({})
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (name && name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInvite = async () => {
    if (!validateForm()) return

    setLoading(true)
    try {
      const newMember = await teamService.inviteTeamMember({
        email: email.trim(),
        name: name.trim() || undefined,
        role,
        message: message.trim() || undefined,
      })

      onMemberInvited?.(newMember)
      setOpen(false)
      resetForm()
    } catch (error) {
      toast.error(extractErrorMessage(error))
      setErrors({ submit: 'Failed to send invitation. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && !loading) {
      resetForm()
    }
    setOpen(newOpen)
  }

  const getRoleIcon = (roleValue: TeamMember['role']) => {
    switch (roleValue) {
      case 'admin':
        return <Crown className='w-4 h-4 text-amber-500' />
      case 'member':
        return <Users className='w-4 h-4 text-blue-500' />
      case 'viewer':
        return <Eye className='w-4 h-4 text-slate-500' />
    }
  }

  const getRoleDescription = (roleValue: TeamMember['role']) => {
    switch (roleValue) {
      case 'admin':
        return 'Full access to all features and settings'
      case 'member':
        return 'Can view and manage most features'
      case 'viewer':
        return 'Read-only access to dashboards and reports'
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <UserPlus className='mr-2 h-4 w-4' />
          Invite Team Member
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Mail className='w-5 h-5 text-blue-500' />
            Invite Team Member
          </DialogTitle>
          <DialogDescription>
            Send an invitation to join your team. They'll receive an email with
            instructions to set up their account.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-6 py-4'>
          {/* Email Field */}
          <div className='space-y-2'>
            <Label htmlFor='email'>
              Email Address <span className='text-red-500'>*</span>
            </Label>
            <Input
              id='email'
              type='email'
              placeholder='colleague@company.com'
              value={email}
              onChange={e => setEmail(e.target.value)}
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <div className='flex items-center gap-1 text-sm text-red-600'>
                <AlertCircle className='w-4 h-4' />
                {errors.email}
              </div>
            )}
          </div>

          {/* Name Field */}
          <div className='space-y-2'>
            <Label htmlFor='name'>Full Name</Label>
            <Input
              id='name'
              type='text'
              placeholder='Optional - they can set this later'
              value={name}
              onChange={e => setName(e.target.value)}
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <div className='flex items-center gap-1 text-sm text-red-600'>
                <AlertCircle className='w-4 h-4' />
                {errors.name}
              </div>
            )}
          </div>

          {/* Role Selection */}
          <div className='space-y-2'>
            <Label htmlFor='role'>Role</Label>
            <Select
              value={role}
              onValueChange={value =>
                setRole(value as 'admin' | 'member' | 'viewer')
              }
            >
              <SelectTrigger>
                <div className='flex items-center gap-2'>
                  {getRoleIcon(role)}
                  <span className='font-medium'>
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </span>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='viewer'>
                  <div className='flex items-center gap-2'>
                    {getRoleIcon('viewer')}
                    <div>
                      <div className='font-medium'>Viewer</div>
                      <div className='text-xs text-muted-foreground'>
                        Read-only access
                      </div>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value='member'>
                  <div className='flex items-center gap-2'>
                    {getRoleIcon('member')}
                    <div>
                      <div className='font-medium'>Member</div>
                      <div className='text-xs text-muted-foreground'>
                        Can manage most features
                      </div>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value='admin'>
                  <div className='flex items-center gap-2'>
                    {getRoleIcon('admin')}
                    <div>
                      <div className='font-medium'>Admin</div>
                      <div className='text-xs text-muted-foreground'>
                        Full access to everything
                      </div>
                    </div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <div className='text-xs text-muted-foreground'>
              {getRoleDescription(role)}
            </div>
          </div>

          {/* Custom Message */}
          <div className='space-y-2'>
            <Label htmlFor='message'>Personal Message</Label>
            <Textarea
              id='message'
              placeholder="Welcome to the team! We're excited to have you..."
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={3}
              className='resize-none'
            />
            <div className='text-xs text-muted-foreground'>
              Optional personal message to include in the invitation email
            </div>
          </div>

          {errors.submit && (
            <div className='flex items-center gap-2 p-3 rounded-md bg-red-50 border border-red-200'>
              <AlertCircle className='w-4 h-4 text-red-600' />
              <span className='text-sm text-red-600'>{errors.submit}</span>
            </div>
          )}
        </div>

        <div className='flex justify-end gap-3'>
          <Button
            variant='outline'
            onClick={() => setOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleInvite} disabled={loading}>
            {loading ? (
              <>
                <RefreshCw className='mr-2 h-4 w-4 animate-spin' />
                Sending Invite...
              </>
            ) : (
              <>
                <Mail className='mr-2 h-4 w-4' />
                Send Invitation
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
